import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Language, translations, Translations } from "@/lib/i18n";
import { LANGUAGES, routePaths, translatePath, localePath, type RouteKey, persistLanguage, resolveInitialLanguage, ensureLanguageSynchronization } from "@/lib/routes";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  /** Generate a localized path: p("about") → "/de/agentur" */
  p: (key: RouteKey, suffix?: string) => string;
}

const defaultContext: LanguageContextType = {
  language: "de",
  setLanguage: () => {},
  t: translations["de"],
  p: (key, suffix) => localePath(key, "de", suffix),
};

const LanguageContext = createContext<LanguageContextType>(defaultContext);

/** Extract language from pathname, e.g. "/de/agentur" → "de" */
function langFromPath(pathname: string): Language | null {
  const seg = pathname.split("/")[1];
  return LANGUAGES.includes(seg as Language) ? (seg as Language) : null;
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const urlLang = langFromPath(location.pathname);

  const [language, setLanguageState] = useState<Language>(() => {
    return resolveInitialLanguage(location.pathname);
  });

  // Sync language from URL changes
  useEffect(() => {
    if (urlLang && urlLang !== language) {
      setLanguageState(urlLang);
      persistLanguage(urlLang);
    }
    ensureLanguageSynchronization(location.pathname);
  }, [urlLang, language]);

  useEffect(() => {
    persistLanguage(language);
    ensureLanguageSynchronization(location.pathname);
  }, [language]);

  // Redirect root "/" to detected language
  useEffect(() => {
    if (location.pathname === "/" || location.pathname === "") {
      navigate(`/${language}`, { replace: true });
    }
  }, [location.pathname, language, navigate]);

  const setLanguage = useCallback((newLang: Language) => {
    if (newLang === language) return;
    setLanguageState(newLang);
    const newPath = translatePath(location.pathname, language, newLang);
    navigate(newPath, { replace: true });
  }, [language, location.pathname, navigate]);

  const t = translations[language];

  const p = useCallback(
    (key: RouteKey, suffix?: string) => localePath(key, language, suffix),
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, p }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
